import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../util/reactIntl';
import {
  getProfileTrustSidebarState,
  hasLiveArrivalGuarantee,
  hasShippingPolicy,
} from '../../config/r2rCommercialSeller';
import { Heading, IconArrowHead, IconShipping, Modal } from '../../components';

import IconShieldCheck from './IconShieldCheck';
import css from './ProfilePoliciesSection.module.css';

/**
 * Profile-tab Shipping & Policies card. Opens policy modals when a row is clicked.
 * Renders nothing when the seller has neither shipping nor LAG configured.
 *
 * @component
 */
const ProfilePoliciesSection = props => {
  const { user, onManageDisableScrolling = () => {}, className } = props;
  const [openModal, setOpenModal] = useState(null);

  const trust = getProfileTrustSidebarState(user);
  const showAny = trust.shipping || trust.liveArrivalGuarantee;
  if (!showAny) {
    return null;
  }

  const publicData = user?.attributes?.profile?.publicData || {};
  const shippingText = hasShippingPolicy(user)
    ? String(publicData.sellerShippingPolicy).trim()
    : '';
  const doaText = hasLiveArrivalGuarantee(user)
    ? String(publicData.sellerDoaReturnPolicy).trim()
    : '';

  const closeModal = () => setOpenModal(null);

  return (
    <section className={classNames(css.root, className)}>
      <h2 className={css.title}>
        <FormattedMessage id="ProfilePage.policiesTitle" />
      </h2>

      <ul className={css.policyList}>
        {trust.shipping ? (
          <li>
            <button
              type="button"
              className={css.policyRow}
              onClick={() => setOpenModal('shipping')}
            >
              <span className={classNames(css.iconCircle, css.shippingIconCircle)} aria-hidden>
                <IconShipping className={css.rowIcon} />
              </span>
              <span className={css.policyRowText}>
                <span className={css.policyRowTitle}>
                  <FormattedMessage id="ProfilePage.policiesShippingTitle" />
                </span>
                <span className={css.policyRowPreview}>{shippingText}</span>
              </span>
              <IconArrowHead className={css.chevron} direction="right" size="tiny" />
            </button>
          </li>
        ) : null}

        {trust.liveArrivalGuarantee ? (
          <li>
            <button type="button" className={css.policyRow} onClick={() => setOpenModal('lag')}>
              <span className={classNames(css.iconCircle, css.lagIconCircle)} aria-hidden>
                <IconShieldCheck className={css.rowIcon} />
              </span>
              <span className={css.policyRowText}>
                <span className={css.policyRowTitle}>
                  <FormattedMessage id="ProfilePage.policiesLagTitle" />
                </span>
                <span className={css.policyRowPreview}>{doaText}</span>
              </span>
              <IconArrowHead className={css.chevron} direction="right" size="tiny" />
            </button>
          </li>
        ) : null}
      </ul>

      <Modal
        id="ProfilePage.shippingPolicy"
        isOpen={openModal === 'shipping'}
        onClose={closeModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        scrollLayerClassName={css.policyScrollLayer}
        containerClassName={css.policyModalContainer}
      >
        <Heading as="h2" rootClassName={css.modalTitle}>
          <FormattedMessage id="ProfilePage.policiesShippingTitle" />
        </Heading>
        <p className={css.modalBody}>{shippingText}</p>
      </Modal>

      <Modal
        id="ProfilePage.liveArrivalGuarantee"
        isOpen={openModal === 'lag'}
        onClose={closeModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        scrollLayerClassName={css.policyScrollLayer}
        containerClassName={css.policyModalContainer}
      >
        <Heading as="h2" rootClassName={css.modalTitle}>
          <FormattedMessage id="ProfilePage.policiesLagTitle" />
        </Heading>
        <p className={css.modalBody}>{doaText}</p>
      </Modal>
    </section>
  );
};

export default ProfilePoliciesSection;
